import React from 'react';
import { useQuery } from '@tanstack/react-query';
import parseByDelimiter from '@utils/parseByDelimiter';
import DropDownPicker from 'react-native-dropdown-picker';
import useGlobalUser from '@hooks/getGlobalUser';
import apis from '@api/api';

type DataSetSelectionProps = {
  changeCallback?: (value: string) => void;
  initialValue?: string | null;
};

const DataSetSelection = React.forwardRef<any, DataSetSelectionProps>((props, ref) => {
  const { changeCallback, initialValue } = props;
  const [selectedDataSet, setSelectedDataSet] = React.useState<string | null>(null);
  const [dataSets, setDataSets] = React.useState<{ label: string; value: string }[]>([]);
  const [open, setOpen] = React.useState(false);
  const selectedId = useGlobalUser();

  const prevValueRef = React.useRef<string | null>(null);
  const isFirstRender = React.useRef(true);

  const { data: dataSetQuery, isLoading } = useQuery({
    queryKey: ['dataFolders'],
    queryFn: async () => {
      const res = await apis.getDataFolders({ userId: selectedId, dataFilter: 'all' });
      return res;
    },
  });

  // Load datasets + apply initial value (if any)
  React.useEffect(() => {
    if (dataSetQuery) {
      const transformed = dataSetQuery.map((item: any) => ({
        label: parseByDelimiter(item.dataTitle),
        value: item._id,
      }));
      setDataSets(transformed);

      if (isFirstRender.current) {
        if (initialValue && transformed.find((d) => d.value === initialValue)) {
          setSelectedDataSet(initialValue);
          prevValueRef.current = initialValue;
        }
        isFirstRender.current = false;
      }
    }
  }, [dataSetQuery]);

  // Trigger change callback on value change (after first render)
  React.useEffect(() => {
    if (
      selectedDataSet &&
      selectedDataSet !== prevValueRef.current &&
      !isFirstRender.current
    ) {
      prevValueRef.current = selectedDataSet;
      changeCallback?.(selectedDataSet);
    }
  }, [selectedDataSet]);

  React.useImperativeHandle(ref, () => ({
    getValue: () => selectedDataSet,
  }));

  return (
    <DropDownPicker
      dropDownDirection="BOTTOM"
      loading={isLoading}
      placeholder="Select a dataset"
      searchable={true}
      dropDownContainerStyle={{ borderWidth: 1 }}
      style={{
        flex: 1,
        minWidth: 300,
        outline: 'none',
        borderWidth: 1,
        zIndex: 100000,
      }}
      value={selectedDataSet}
      items={dataSets}
      setValue={(val) => {
        if (val !== selectedDataSet) setSelectedDataSet(val);
      }}
      open={open}
      setOpen={setOpen}
      onChangeValue={(val) => {
        if (val !== selectedDataSet) setSelectedDataSet(val);
      }}
    />
  );
});

export default DataSetSelection;
